import React, { useState } from 'react';
import { 
  Building2, 
  Search, 
  Bed, 
  SwitchCamera, 
  CornerDownRight, 
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  setDoc, 
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { firestore } from '../lib/firebase/config';
import { normalizePassportNumber } from '../utils/passportUtils';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

type RoomType = 'double' | 'triple' | 'quadruple';
type ChangeReason = 'nusuk_misleading' | 'switch_pilgrims' | 'other';

type HotelField = {
  key: 'mainMakkah' | 'shiftingMakkah' | 'madinah';
  selectorKey: 'mainMakkah' | 'shiftingMakkah' | 'madinah';
  pilgrimKey: 'main_makkah_roomType' | 'shifting_makkah_roomType' | 'madinah_roomType';
  fieldName: string;
};

type Hotel = {
  name: string;
  currentRoomType: RoomType | null;
  field: HotelField;
};

export const RoomTypeSwitchPage = () => {
  const { translations } = useLanguage();
  const [passportNumber, setPassportNumber] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [pilgrim, setPilgrim] = useState<any | null>(null);
  const [pilgrimSelector, setPilgrimSelector] = useState<any | null>(null);
  const [availableHotels, setAvailableHotels] = useState<Hotel[]>([]);
  const [selectedHotel, setSelectedHotel] = useState<string>('');
  const [selectedRoomType, setSelectedRoomType] = useState<RoomType | ''>('');
  const [changeReason, setChangeReason] = useState<ChangeReason>('nusuk_misleading');
  const [otherReason, setOtherReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if user is admin
  const userRole = localStorage.getItem('userRole');
  const userName = localStorage.getItem('userName');
  const userId = localStorage.getItem('userId');
  
  const isAdmin = userRole === 'admin';

  const handleSearch = async () => {
    if (!passportNumber.trim()) {
      toast.error('Please enter a passport number');
      return;
    }

    setIsSearching(true);
    setError(null);
    setPilgrim(null);
    setPilgrimSelector(null);
    setAvailableHotels([]);
    setSelectedHotel('');
    setSelectedRoomType('');
    setSuccess(false);

    try {
      const normalizedPassport = normalizePassportNumber(passportNumber);
      
      // Search for pilgrim in Pilgrims collection
      const pilgrimsRef = collection(firestore, 'Pilgrims');
      const pilgrimQuery = query(pilgrimsRef, where('Passport_Number', '==', normalizedPassport));
      const pilgrimSnapshot = await getDocs(pilgrimQuery);
      
      if (pilgrimSnapshot.empty) {
        setError('No pilgrim found with this passport number');
        setIsSearching(false);
        return;
      }
      
      // Get pilgrim data
      const pilgrimDoc = pilgrimSnapshot.docs[0];
      const pilgrimData = { id: pilgrimDoc.id, ...pilgrimDoc.data() };
      setPilgrim(pilgrimData);
      
      // Get PilgrimSelector data
      const selectorRef = collection(firestore, 'PilgrimSelector');
      const selectorQuery = query(selectorRef, where('pilgrimId', '==', pilgrimDoc.id));
      const selectorSnapshot = await getDocs(selectorQuery);
      
      if (selectorSnapshot.empty) {
        setError('Pilgrim not connected to any guide (no selector data found)');
        setIsSearching(false);
        return;
      }
      
      // Get selector data
      const selectorDoc = selectorSnapshot.docs[0];
      const selectorData = { id: selectorDoc.id, ...selectorDoc.data() };
      setPilgrimSelector(selectorData);
      
      // Get available hotels from package data
      const hotels: Hotel[] = [];
      
      const hotelFields: HotelField[] = [
        { 
          key: 'mainMakkah', 
          selectorKey: 'mainMakkah', 
          pilgrimKey: 'main_makkah_roomType',
          fieldName: 'Main Makkah' 
        },
        { 
          key: 'shiftingMakkah', 
          selectorKey: 'shiftingMakkah', 
          pilgrimKey: 'shifting_makkah_roomType',
          fieldName: 'Shifting Makkah' 
        },
        { 
          key: 'madinah', 
          selectorKey: 'madinah', 
          pilgrimKey: 'madinah_roomType',
          fieldName: 'Madinah' 
        }
      ];
      
      hotelFields.forEach(field => {
        // Check if this hotel exists in the selector data
        const hotelData = selectorData.packageData[field.key];
        if (hotelData && hotelData.hotel) {
          // Current room type from pilgrim data
          let currentRoomType = pilgrimData[field.pilgrimKey] as RoomType;
          
          // If shifting_makkah_roomType is missing or empty, use a default
          if (field.pilgrimKey === 'shifting_makkah_roomType' && (!currentRoomType || currentRoomType === '')) {
            currentRoomType = 'triple'; // Default to triple if missing
          }
          
          hotels.push({
            name: hotelData.hotel,
            currentRoomType: currentRoomType as RoomType,
            field: field
          });
        }
      });
      
      setAvailableHotels(hotels);
    } catch (error) {
      console.error('Error searching for pilgrim:', error);
      setError('An error occurred while searching for the pilgrim');
    } finally {
      setIsSearching(false);
    }
  };
  
  const handleSwitchRoomType = async () => {
    if (!selectedHotel || !selectedRoomType) {
      toast.error('Please select a hotel and room type');
      return;
    }
    
    if (changeReason === 'other' && !otherReason.trim()) {
      toast.error('Please provide a reason for the change');
      return;
    }
    
    setIsProcessing(true);
    setError(null);
    
    try {
      // Find the hotel details
      const hotel = availableHotels.find(h => h.name === selectedHotel);
      
      if (!hotel) {
        throw new Error('Selected hotel not found');
      }
      
      // Check if there's actually a change
      if (hotel.currentRoomType === selectedRoomType) {
        toast.info('No change required - the room type is already set to ' + selectedRoomType);
        setIsProcessing(false);
        return;
      }
      
    // Prepare batch updates
const batch = writeBatch(firestore);

// 1. Update pilgrim document
const pilgrimRef = doc(firestore, 'Pilgrims', pilgrim.id);
batch.update(pilgrimRef, {
  [hotel.field.pilgrimKey]: selectedRoomType,
  updatedAt: Date.now()
});

// 2. Update PilgrimSelector document
const selectorRef = doc(firestore, 'PilgrimSelector', pilgrimSelector.id);
const packageDataPath = `packageData.${hotel.field.selectorKey}.roomType`;
const hotelRoomsUpdate = { 
  [`hotelRooms.${hotel.name}.roomType`]: selectedRoomType,
  updatedAt: Date.now()
};
batch.update(selectorRef, {
  [packageDataPath]: selectedRoomType,
  ...hotelRoomsUpdate
});

// 3. Record the change in roomtypechange collection
const changeRef = doc(collection(firestore, 'roomtypechange'));
const reasonText = changeReason === 'other' 
  ? otherReason
  : changeReason === 'nusuk_misleading'
    ? 'Nusuk misleading data'
    : 'Switch pilgrims roomtypes';

const changeRecord = {
  id: changeRef.id,
  pilgrimId: pilgrim.id,
  pilgrimName: pilgrim.Name,
  passportNumber: pilgrim.Passport_Number,
  hotelName: hotel.name,
  oldRoomType: hotel.currentRoomType,
  newRoomType: selectedRoomType,
  reason: changeReason,
  reasonText,
  otherReason: changeReason === 'other' ? otherReason : null,
  changedBy: userId,
  changedByName: userName,
  timestamp: Date.now(),
};
batch.set(changeRef, changeRecord);

// 4. Remove pilgrim from AccommodationTables
const tablesRef = collection(firestore, 'AccommodationTables');
const tablesSnapshot = await getDocs(tablesRef);

tablesSnapshot.forEach(tableDoc => {
  const data = tableDoc.data();
  if (data.hn !== hotel.name) return;

  const updatedRooms = data.r.map((room: any) => {
    const updatedBeds = room.b.map((bed: any) => {
      if (!bed.a) return bed;
      const newAssignments = bed.a.filter((a: any) => a.pid !== pilgrim.id);
      return { ...bed, a: newAssignments };
    });

    const newOccupancy = updatedBeds.reduce((acc: number, bed: any) => acc + (bed.a?.length || 0), 0);

    return { ...room, b: updatedBeds, o: newOccupancy };
  });

  batch.update(tableDoc.ref, {
    r: updatedRooms,
    ua: Date.now()
  });
});

// Commit batch
await batch.commit();

      
      // Update state with new room type
      setAvailableHotels(prev => 
        prev.map(h => 
          h.name === selectedHotel 
            ? { ...h, currentRoomType: selectedRoomType as RoomType } 
            : h
        )
      );
      
      setSelectedHotel('');
      setSelectedRoomType('');
      setSuccess(true);
      toast.success('Room type changed successfully');
    } catch (error: any) {
      console.error('Error changing room type:', error);
      setError(error.message || 'Failed to change room type');
      toast.error('Failed to change room type');
    } finally {
      setIsProcessing(false);
    }
  };
  
  // Only admins can access this page
  if (!isAdmin) {
    return (
      <div className="bg-white p-8 rounded-xl shadow-sm text-center">
        <AlertCircle className="mx-auto mb-4 text-red-500" size={64} />
        <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
        <p className="text-gray-600">You do not have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-xl shadow-sm">
        <h1 className="text-2xl font-bold mb-1">Room Type Switch</h1>
        <p className="text-gray-600 mb-6">Change pilgrim's room type across all system data</p>
        
        {/* Search Form */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <input
              type="text"
              value={passportNumber}
              onChange={(e) => setPassportNumber(e.target.value)}
              placeholder="Enter pilgrim's passport number..."
              className="w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          </div>
          
          <button 
            onClick={handleSearch}
            disabled={isSearching || !passportNumber.trim()}
            className={`px-6 py-3 rounded-lg flex items-center gap-2 
              ${isSearching || !passportNumber.trim() 
                ? 'bg-gray-300 cursor-not-allowed text-gray-500' 
                : 'bg-primary-500 text-white hover:bg-primary-600'}`}
          >
            {isSearching ? (
              <>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                <span>Searching...</span>
              </>
            ) : (
              <>
                <Search size={20} />
                <span>Search</span>
              </>
            )}
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 p-4 rounded-lg flex items-start gap-3 text-red-700 mb-6">
            <AlertCircle className="shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium">Error</p>
              <p>{error}</p>
            </div>
          </div>
        )}
        
        {/* Success Message */}
        {success && (
          <div className="bg-green-50 p-4 rounded-lg flex items-start gap-3 text-green-700 mb-6">
            <CheckCircle className="shrink-0 mt-0.5" size={20} />
            <div>
              <p className="font-medium">Success</p>
              <p>Room type has been changed successfully</p>
            </div>
            <button 
              onClick={() => setSuccess(false)} 
              className="ml-auto text-green-700 hover:text-green-800 p-1"
            >
              <X size={16} />
            </button>
          </div>
        )}
        
        {/* Pilgrim Details */}
        {pilgrim && !error && (
          <div className="bg-gray-50 p-6 rounded-lg mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Building2 size={20} className="text-primary-500" />
              <span>Pilgrim Details</span>
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-medium">{pilgrim.Name}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Passport Number</p>
                <p className="font-medium">{pilgrim.Passport_Number}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="font-medium">{pilgrim.Gender}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Country</p>
                <p className="font-medium">{pilgrim.Residence_Country}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Package</p>
                <p className="font-medium">{pilgrim.package_nickName}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Guide</p>
                <p className="font-medium">{pilgrimSelector?.guideData?.name}</p>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Organization</p>
                <p className="font-medium">{pilgrimSelector?.organizerData?.name}</p>
              </div>
            </div>
          </div>
        )}
        
        {/* Hotels and Room Types */}
        {availableHotels.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-4">Current Room Assignments</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {availableHotels.map(hotel => (
                <div 
                  key={hotel.name} 
                  className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="bg-primary-50 p-4 border-b">
                    <h3 className="font-medium">{hotel.name}</h3>
                    <p className="text-sm text-gray-600">{hotel.field.fieldName}</p>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm text-gray-500">Current Room Type</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Bed size={16} className="text-primary-500" />
                          <span className="font-medium">{hotel.currentRoomType || 'Not set'}</span>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => {
                          setSelectedHotel(hotel.name);
                          setSelectedRoomType(hotel.currentRoomType || '');
                        }}
                        className="px-3 py-1.5 text-primary-600 bg-primary-50 rounded-lg text-sm hover:bg-primary-100"
                      >
                        Select
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Room Type Change Form */}
            {selectedHotel && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-primary-50 p-6 rounded-lg border border-primary-200"
              >
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <SwitchCamera size={20} className="text-primary-500" />
                  <span>Change Room Type</span>
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  {/* Selected Hotel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Selected Hotel
                    </label>
                    <div className="flex items-center gap-2 p-2 border rounded-lg bg-white">
                      <Building2 size={16} className="text-primary-500" />
                      <span>{selectedHotel}</span>
                    </div>
                  </div>
                  
                  {/* New Room Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Room Type
                    </label>
                    <select
                      value={selectedRoomType}
                      onChange={(e) => setSelectedRoomType(e.target.value as RoomType)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select Room Type</option>
                      <option value="double">Double</option>
                      <option value="triple">Triple</option>
                      <option value="quadruple">Quadruple</option>
                    </select>
                  </div>
                  
                  {/* Change Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reason for Change
                    </label>
                    <select
                      value={changeReason}
                      onChange={(e) => setChangeReason(e.target.value as ChangeReason)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="nusuk_misleading">Nusuk Misleading Data</option>
                      <option value="switch_pilgrims">Switch Pilgrims Roomtypes</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                
                {/* Other Reason Text Input */}
                {changeReason === 'other' && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Specify Reason
                    </label>
                    <textarea
                      value={otherReason}
                      onChange={(e) => setOtherReason(e.target.value)}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder="Please specify the reason for changing the room type..."
                    ></textarea>
                  </div>
                )}
                
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setSelectedHotel('');
                      setSelectedRoomType('');
                    }}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={handleSwitchRoomType}
                    disabled={isProcessing || !selectedRoomType}
                    className={`px-6 py-2 rounded-lg flex items-center gap-2
                      ${isProcessing || !selectedRoomType
                        ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                        : 'bg-primary-500 text-white hover:bg-primary-600'}`}
                  >
                    {isProcessing ? (
                      <>
                        <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <SwitchCamera size={20} />
                        <span>Switch Room Type</span>
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        )}
      </div>
      
      {/* Changes History Table */}
     <RoomTypeSwitchHistory />
    </div>
  );
};

// Component to display room type change history
const RoomTypeSwitchHistory = ({ pilgrimId }: { pilgrimId?: string }) => {
  const [changes, setChanges] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    const fetchChanges = async () => {
      setIsLoading(true);
      try {
        const changesRef = collection(firestore, 'roomtypechange');
        const changesQuery = pilgrimId
          ? query(changesRef, where('pilgrimId', '==', pilgrimId))
          : query(changesRef); // no filter = fetch all

        const changesSnapshot = await getDocs(changesQuery);
        const changesData = changesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        changesData.sort((a, b) => b.timestamp - a.timestamp);
        setChanges(changesData);
      } catch (error) {
        console.error('Error fetching room type changes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchChanges();
  }, [pilgrimId]);

  if (!changes.length && !isLoading) return null;

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <h2 className="text-lg font-semibold mb-4">Room Type Change History</h2>

      {isLoading ? (
        <div className="py-8 text-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pilgrim</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hotel</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Changed By</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {changes.map(change => (
                <tr key={change.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(change.timestamp).toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {change.pilgrimName || 'Unknown'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {change.hotelName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium bg-blue-50 text-blue-700 px-2 py-1 rounded-lg">
                        {change.oldRoomType}
                      </span>
                      <CornerDownRight size={16} className="text-gray-400" />
                      <span className="text-sm font-medium bg-green-50 text-green-700 px-2 py-1 rounded-lg">
                        {change.newRoomType}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {change.reasonText}
                    {change.otherReason && (
                      <div className="mt-1 text-xs italic text-gray-500">
                        "{change.otherReason}"
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {change.changedByName || 'Unknown'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};


export default RoomTypeSwitchPage;